import isNumber from 'lodash.isnumber';
import Immutable from 'seamless-immutable';
import moment from 'moment';
import React from 'react';
import { injectIntl, defineMessages, FormattedMessage, FormattedDate } from 'react-intl';
import fileSize from 'filesize';
import _extends from '@babel/runtime/helpers/esm/extends';
import queryString from 'query-string';
import moment$1 from 'moment-timezone';

/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2020 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

/**
 * Ensure that the provided value is an array
 *
 * @param array
 * @returns {[]} Original array or original value as single element in array
 */
function ensureArray(array) {
  return Array.isArray(array) ? array : [array];
}

/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2020 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
function getTotalPages(pageConfig = Immutable({})) {
  const {
    entities,
    limit,
    page,
    totalResults
  } = pageConfig;

  if (!isNumber(limit)) {
    return undefined;
  }

  const pageSize = entities ? entities.length : undefined;

  // undefined !== number
  // If we don't know how many total records the collection has, but we do have entities on
  // the current page (and they are fewer than the limit), the current page is the last page.
  if (!isNumber(totalResults) && pageSize && !(pageSize >= limit) && isNumber(page)) {
    return page;
  }

  return isNumber(totalResults) ? Math.ceil(totalResults / limit) : undefined;
}

/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2020 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
/**
 * Returns a promise that resolves once the finished condition is truthy,
 * re-evaluating at the given interval for the given length of time, at
 * which point the promise is rejected with an error using the given timeout error message.
 */

function polling ({
  interval,
  duration,
  isFinished,
  timeoutErrorMessage = 'Polling timed out'
}) {
  const endTime = moment().add(duration, 'milliseconds');

  const poller = async (resolve, reject) => {
    try {
      const result = await isFinished();

      if (result) {
        resolve(result);
      } else if (moment().isBefore(endTime)) {
        setTimeout(poller, interval, resolve, reject);
      } else {
        reject(timeoutErrorMessage);
      }
    } catch (error) {
      reject(error);
    }
  };

  return new Promise(poller);
}

/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2020 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
const createInvalidPropTypeErrorMessage = reactElement => {
  return `The property type must be ${reactElement.displayName}.`;
};
const createRequiredErrorMessage = reactElement => {
  return `The ${reactElement.displayName} property type is required.`;
}; // Reference: https://www.ian-thomas.net/custom-proptype-validation-with-react/

function reactElementPropType(reactElement) {
  const checkPropTypeMatch = (props, propName) => {
    const prop = props[propName];

    if (reactElement.constructor !== (prop.type && prop.type.constructor)) {
      return new Error(createInvalidPropTypeErrorMessage(reactElement));
    }

    return null;
  };

  const checkType = (isRequired, props, propName) => {
    const prop = props[propName];

    if (prop) {
      return checkPropTypeMatch(props, propName);
    } else if (isRequired) {
      return new Error(createRequiredErrorMessage(reactElement));
    }

    return null;
  };

  const chainedCheckType = checkType.bind(null, false);
  chainedCheckType.isRequired = checkType.bind(null, true);
  return chainedCheckType;
}

/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2020 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
/**
 * Make sure the value is either a string or converts a formatted message to a string
 *
 * @param {object} intl the intl object
 * @param {?object|string} propVal value to ensure is string
 * @returns {string}
 */

const ensureString = ({
  formatMessage
}, propVal) => {
  if ( // a FormattedMessage requires an id and defaultMessage for our harvesters
  // and FormattedMessage has a default for the values field so it should always be there
  typeof propVal === 'object' && propVal.props && propVal.props.id && propVal.props.defaultMessage && propVal.props.values !== undefined) {
    propVal = formatMessage({ ...propVal.props
    });
  }

  if (typeof propVal !== 'string') {
    throw new Error(`prop expected to be FormattedMessage (FM) or string.`);
  }

  return propVal;
};
/**
 * Translate the list of props from an FM to a string
 *
 * @param {[]}
 * @returns Component wrapped component
 */

const translatePropsToStrings = propsToTranslate => Component => injectIntl(({
  intl,
  ...props
}) => {
  propsToTranslate.forEach(propName => {
    // allow undefined or null props to pass through to component
    // so it can decide whether they're required
    if (props[propName]) {
      props[propName] = ensureString(intl, props[propName]);
    }
  });
  return /*#__PURE__*/React.createElement(Component, props);
});

/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2020 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
const UNITS = {
  24: 'Y',
  21: 'Z',
  18: 'E',
  15: 'P',
  12: 'T',
  9: 'B',
  6: 'M',
  3: 'k',
  0: '',
  '-3': 'm',
  '-6': 'µ',
  '-9': 'n',
  '-12': 'p',
  '-15': 'f',
  '-18': 'a',
  '-21': 'z',
  '-24': 'y'
};
/**
 * Shortens numbers and appends SI prefix to the end. The SI prefix is intended to be international, but quite a few of
 * the units beyond 'M' and when dealing with small decimals are a bit unknown. Need to vet this through with intl team.
 * This is done to prevent axis numbers on charts from getting really large and unreadable.
 *
 * @param intl A react-intl object received from injectIntl.
 * @param num The number to be shortened.
 */

function getShortenedNumberComponents(intl, num) {
  if (num === 0) {
    return {
      value: 0
    };
  }

  let significand = Math.abs(num);
  let exponent = 0;

  while (significand >= 1000 && exponent < 24) {
    significand /= 1000;
    exponent += 3;
  }

  while (significand < 1 && exponent > -24) {
    significand *= 1000;
    exponent -= 3;
  }

  const unit = UNITS[exponent];
  const modifier = num < 0 ? -1 : 1;

  if (significand > 1000) {
    // exponent == 24
    return {
      unit,
      value: (modifier * significand).toFixed(0)
    };
  }

  if (num < 1 && num > 0.01) {
    // Don't use 500m, instead do 0.5 as that number is still quite short.
    return {
      value: num
    };
  }

  return {
    unit,
    value: intl.formatNumber(parseFloat(modifier * significand), {
      maximumSignificantDigits: 3
    })
  };
}
function shortenBytes(intl, num, filesizeOpts = {}, numberOpts = {}) {
  const {
    value,
    suffix
  } = fileSize(num, {
    output: 'object',
    locale: intl.locale,
    ...filesizeOpts
  });
  const size = intl.formatNumber(value, numberOpts);
  return `${size} ${suffix}`;
}
function shortenNumber(intl, num) {
  const {
    unit = '',
    value
  } = getShortenedNumberComponents(intl, num);
  return value + unit;
}
function millisecondToHumanReadableTime(millisec) {
  if (!isNumber(millisec)) {
    return {
      value: millisec,
      unit: ''
    };
  }

  const seconds = (millisec / 1000).toFixed(1);
  const minutes = (millisec / (1000 * 60)).toFixed(1);
  const hours = (millisec / (1000 * 60 * 60)).toFixed(1);
  const days = (millisec / (1000 * 60 * 60 * 24)).toFixed(1);

  if (seconds < 1) {
    return {
      value: millisec,
      unit: 'millisecond'
    };
  } else if (seconds < 60) {
    return {
      value: seconds,
      unit: 'second'
    };
  } else if (minutes < 60) {
    return {
      value: minutes,
      unit: 'minute'
    };
  } else if (hours < 24) {
    return {
      value: hours,
      unit: 'hour'
    };
  }

  return {
    value: days,
    unit: 'day'
  };
}

const LAST_3_DAYS = 'last3Days';
const LAST_7_DAYS = 'last7Days';
const LAST_30_DAYS = 'last30Days';
const LAST_60_DAYS = 'last60Days';
const THIS_MONTH = 'thisMonth';
const LAST_MONTH = 'lastMonth';
const messages$1 = defineMessages({
  [LAST_3_DAYS]: {
    id: 'date_range_preset.last_3_days',
    defaultMessage: 'Last 3 days'
  },
  [LAST_7_DAYS]: {
    id: 'date_range_preset.last_7_days',
    defaultMessage: 'Last 7 days'
  },
  [LAST_30_DAYS]: {
    id: 'date_range_preset.last_30_days',
    defaultMessage: 'Last 30 days'
  },
  [LAST_60_DAYS]: {
    id: 'date_range_preset.last_60_days',
    defaultMessage: 'Last 60 days'
  },
  [THIS_MONTH]: {
    id: 'date_range_preset.this_month',
    defaultMessage: 'This month'
  },
  [LAST_MONTH]: {
    id: 'date_range_preset.last_month',
    defaultMessage: 'Last month'
  }
});
const getDateRangePreset = key => ({
  id: key,
  label: /*#__PURE__*/React.createElement(FormattedMessage, messages$1[key])
});
const getDateRangePresets = () => {
  return [getDateRangePreset(LAST_7_DAYS), getDateRangePreset(LAST_30_DAYS), getDateRangePreset(LAST_60_DAYS), getDateRangePreset(THIS_MONTH), getDateRangePreset(LAST_MONTH)];
};
const getDatesFromRangeId = rangeId => {
  switch (rangeId) {
    case LAST_3_DAYS:
      return {
        start: moment().subtract(2, 'day').startOf('day'),
        end: moment().endOf('day')
      };

    case LAST_7_DAYS:
      return {
        start: moment().subtract(6, 'day').startOf('day'),
        end: moment().endOf('day')
      };

    case LAST_30_DAYS:
      return {
        start: moment().subtract(29, 'day').startOf('day'),
        end: moment().endOf('day')
      };

    case LAST_60_DAYS:
      return {
        start: moment().subtract(59, 'day').startOf('day'),
        end: moment().endOf('day')
      };

    case THIS_MONTH:
      return {
        start: moment().startOf('month'),
        end: moment().endOf('month')
      };

    case LAST_MONTH:
      return {
        start: moment().subtract(1, 'month').startOf('month'),
        end: moment().subtract(1, 'month').endOf('month')
      };

    default:
      throw new Error('Unknown RangeID');
  }
};
const getMatchingRangeFromDates = dateRange => {
  if (!dateRange || !dateRange.start || !dateRange.end) {
    throw new Error('Invalid dateRange');
  }

  const endDate = dateRange.end.clone().startOf('day');
  const startDate = dateRange.start.clone().startOf('day');
  const today = moment().startOf('day');

  if (+startDate === +today.clone().startOf('month') && +endDate === +today.clone().endOf('month').startOf('day')) {
    return THIS_MONTH;
  }

  if (+startDate === +today.clone().subtract(1, 'month').startOf('month') && +endDate === +today.clone().subtract(1, 'month').endOf('month').startOf('day')) {
    return LAST_MONTH;
  }

  if (+endDate === +today) {
    const numDays = endDate.diff(startDate, 'day') + 1; // adding 1 to account for the current selected day.

    switch (numDays) {
      case 3:
        return LAST_3_DAYS;

      case 7:
        return LAST_7_DAYS;

      case 30:
        return LAST_30_DAYS;

      case 60:
        return LAST_60_DAYS;

    }
  }

  return null;
};
const defaultDateFormatOptions = {
  year: 'numeric',
  month: 'long',
  day: 'numeric'
};
const getRangeTextFromDates = (dateRange, short = false, options = defaultDateFormatOptions) => {
  const rangeId = getMatchingRangeFromDates(dateRange);

  if (short && rangeId) {
    return /*#__PURE__*/React.createElement(FormattedMessage, messages$1[rangeId]);
  }

  return /*#__PURE__*/React.createElement(React.Fragment, null, rangeId && [/*#__PURE__*/React.createElement(FormattedMessage, _extends({}, messages$1[rangeId], {
    key: messages$1[rangeId].id
  })), ': '], /*#__PURE__*/React.createElement(FormattedMessage, {
    id: "date_range.range_between_two_dates",
    defaultMessage: "{startDate} - {endDate}",
    values: {
      startDate: /*#__PURE__*/React.createElement(FormattedDate, _extends({
        value: dateRange.start
      }, options)),
      endDate: /*#__PURE__*/React.createElement(FormattedDate, _extends({
        value: dateRange.end
      }, options))
    }
  }));
};
const demomentifyDateRange = ({
  start,
  end
}) => {
  if (!start || typeof start.toISOString !== 'function' || !end || typeof end.toISOString !== 'function') {
    throw new Error('dateRange properties "start" and "end" must be moment objects');
  }

  return {
    start: start.toISOString(),
    end: end.toISOString()
  };
};
const momentifyDateRange = dateRange => {
  if (!dateRange) {
    return dateRange;
  }

  const {
    start,
    end
  } = dateRange;
  return {
    start: moment.isMoment(start) ? start : moment(start),
    end: moment.isMoment(end) ? end : moment(end)
  };
};
const areSameDateRanges = (dateRange1, dateRange2) => {
  const momentifiedDateRange1 = momentifyDateRange(dateRange1);
  return momentifiedDateRange1.start.isSame(dateRange2.start) && momentifiedDateRange1.end.isSame(dateRange2.end);
};

/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2020 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
/**
 * Helper for seamless-immutable that will remove a property at a path.
 *
 * @param obj {Immutable}
 * @param path {Array} 
 * @returns {Immutable} Input object without the property at the specified path
 */

function withoutIn(obj, path) {
  obj = Immutable(obj);
  const key = path[path.length - 1]; // last element

  path = path.slice(0, -1); // All but last element

  if (!path.length) {
    return obj.without(key);
  }

  return obj.update(path, subObject => subObject.without(key));
}

/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2020 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
/**
 * Converts the given parameters object into a string in the format:
 * "paramKey1=paramValue1&paramKey2=paramValue2&..."
 *
 * Will skip parameters whose values are undefined or null
 *
 * Object paramter is a 'query-string' parameter, defaults same as
 * query string https://www.npmjs.com/package/query-string
 *
 * @param queryParams {Object}
 * @param options {Object}
 * @returns {string}
 */

const stringifyParams = (queryParams = {}, options = {}) => {
  const cleanParams = Object.keys(queryParams).reduce((memo, key) => {
    let value = queryParams[key]; // NOTE: need to update query-string version to at least 6.4 to use 'comma' option
    // For now, create own function, but use similar parameter

    if (options.arrayFormat === 'comma' && Array.isArray(value)) {
      value = value.length ? value.join(',') : null;
    }

    if (value != null) {
      memo[key] = value;
    }

    return memo;
  }, {});
  return queryString.stringify(cleanParams, options);
};
const parseQuery = (str, options = {}) => queryString.parse(str, options);
const parseUrl = (str, options = {}) => queryString.parseUrl(str, options);
/**
 * De-serializes a query string into an object using a mapping configuration.
 *
 * Configuration objects are defined as
 *
 * {
 *   entities: { exclude: true },
 *   limit: { number: true },
 *   page: { number: true },
 *   sortDescending: { boolean: true, path: [ 'sort', 0, 'descending' ] },
 *   sortField: { path: [ 'sort', 0, 'field' ] },
 *   '*': ['filters']
 * }
 *
 * where the key is the name used in the query string.
 *
 * The value is a mapping object that is used for determining final location of
 * the property in the output object and whether the property should be treated as a
 * boolean or number.
 *
 * A special wildcard key '*' may be used that only accepts a path. This may be used
 * to place extra properties in a specific path. By default, extra properties will
 * be added to the root of the object.
 *
 * NOTE: Arrays are not currently supported. If the property must be an array, this must
 * be done manually.
 *
 * @param search {String}
 * @param map {Object}
 * @returns {Immutable}
 */

function fromQueryString(search, map) {
  const queryParams = queryString.parse(search);

  if (!map) {
    return Immutable(queryParams);
  }

  const wildcardPath = ensureArray(map['*'] || []);
  const initialObject = wildcardPath.length ? Immutable({}).setIn(wildcardPath, queryParams) : Immutable({});
  return Object.keys(map).reduce((obj, key) => {
    const {
      boolean,
      number,
      path = [key]
    } = map[key];
    let value = queryParams[key]; // If the query string didn't contain the property, or it's empty remove it
    // from the wildcard path and return

    if (!value) {
      return withoutIn(obj, [...wildcardPath, key]);
    } // Parse the query parameter into a boolean or number if present


    if (boolean) {
      value = value.toLowerCase() === 'true' || value === '1';
    } else if (number) {
      value = parseInt(value, 10);
    } // Remove value from wildcard path and add to configured path


    return withoutIn(obj, [...wildcardPath, key]).setIn(path, value);
  }, initialObject);
}
/**
 * Serializes an object to a query parameters object using a mapping configuration.
 *
 * Configuration objects are defined as
 *
 * {
 *   entities: { exclude: true },
 *   limit: { number: true },
 *   page: { number: true },
 *   sortDescending: { boolean: true, path: [ 'sort', 0, 'descending' ] },
 *   sortField: { path: [ 'sort', 0, 'field' ] },
 *   '*': ['filters']
 * }
 *
 * where the key is the name used in the query string.
 *
 * The value is a mapping object that is used for determining the location of
 * the property in the input object and whether the property should be treated as a
 * boolean or number.
 *
 * The `.number` property in a mapping object must be a boolean.
 *
 * The `.exclude` property is used to exclude properties of the input object from
 * the output query params object.
 *
 * A special wildcard key '*' may be used that only accepts a path. This may be used
 * to extract all properties from a specific path.
 *
 * This function yields a flattened object that can be passed to `queryString.stringify()`.
 *
 * @param object {Object}
 * @param map {Object}
 * @returns {Object}
 */

function toQueryParams(object = {}, map) {
  object = Immutable(object);
  const wildcardPath = ensureArray(map['*'] || []);
  const initialObject = wildcardPath.length ? object.getIn(wildcardPath) : Immutable({});
  return Object.keys(map).reduce((obj, key) => {
    const {
      boolean,
      exclude,
      path = [key]
    } = map[key];
    const value = object.getIn(path);

    if (exclude || value === undefined || value === null) {
      return obj;
    }

    if (boolean) {
      return obj.set(key, value ? 1 : 0);
    }

    return obj.set(key, value);
  }, initialObject);
}

const messages = defineMessages({
  required: {
    id: 'validation.required',
    defaultMessage: 'Required'
  },
  maxLength: {
    id: 'validation.max_length',
    defaultMessage: 'Must be less than { length } characters'
  },
  numeric: {
    id: 'validation.numeric',
    defaultMessage: 'Must be a number'
  },
  isGreaterThanOrEqualToday: {
    id: 'validation.today_or_future',
    defaultMessage: "Select today's date or a date in the future."
  },
  isGreaterThanOrEqualStartDate: {
    id: 'validation.greater_or_equal_startdate',
    defaultMessage: 'Select a date greater than or equal to start date.'
  }
});
const onlyWhitespaceRegex = /^\s+$/;
const required = () => value => {
  return value != null && value !== '' && !onlyWhitespaceRegex.test(value) ? null : /*#__PURE__*/React.createElement(FormattedMessage, messages.required);
};

const matchesRegex = (regex, error) => value => {
  return value == null || value === '' || regex.test(value) ? null : error;
};

const numericRegex = /^[0-9]+$/;
const numeric = () => matchesRegex(numericRegex, /*#__PURE__*/React.createElement(FormattedMessage, messages.numeric));
const maxLength = length => value => {
  return value == null || value.length <= length ? null : /*#__PURE__*/React.createElement(FormattedMessage, _extends({}, messages.maxLength, {
    values: {
      length
    }
  }));
};
const isGreaterThanOrEqualToday = () => {
  return (value, otherProps = {}) => {
    const {
      timezone = 'UTC'
    } = otherProps;
    const now = moment$1().tz(timezone).format('YYYY-MM-DD');

    if (moment$1(value).tz(timezone).isSameOrAfter(now, 'day')) {
      return null;
    }

    return /*#__PURE__*/React.createElement(FormattedMessage, messages.isGreaterThanOrEqualToday);
  };
};
const isGreaterThanOrEqualStartDate = startDateFieldName => {
  return (value, otherProps = {}, formValues) => {
    if (!value || value === '') {
      return null;
    }

    const {
      timezone = 'UTC'
    } = otherProps;
    const startDateValue = moment$1(formValues[startDateFieldName]).tz(timezone).format('YYYY-MM-DD');

    if (moment$1(value).isSameOrAfter(startDateValue, 'day')) {
      return null;
    }

    return /*#__PURE__*/React.createElement(FormattedMessage, messages.isGreaterThanOrEqualStartDate);
  };
};

export { LAST_30_DAYS, LAST_3_DAYS, LAST_60_DAYS, LAST_7_DAYS, LAST_MONTH, THIS_MONTH, areSameDateRanges, demomentifyDateRange, ensureArray, fromQueryString, getDateRangePreset, getDateRangePresets, getDatesFromRangeId, getMatchingRangeFromDates, getRangeTextFromDates, getShortenedNumberComponents, getTotalPages, isGreaterThanOrEqualStartDate, isGreaterThanOrEqualToday, maxLength, messages$1 as messages, millisecondToHumanReadableTime, momentifyDateRange, numeric, parseQuery, parseUrl, polling, reactElementPropType, required, shortenBytes, shortenNumber, stringifyParams, toQueryParams, translatePropsToStrings };
